// <u5-viewer> — Universal 5.0 in 3D: card + three swappable KITs, each with two rotate-lock plates.
// Card mm are the single source of truth for markers (see https://mrf.tools/cdn/shop/files/mrf26-card-map.b3.js?v=b3).
// Reads tools from window.U5, selection from window.__u5active, placement mode from window.__u5cal.
(function () {
  const THREE_URL = 'https://unpkg.com/three@0.184.0/build/three.module.js';
  const W = 85.4, H = 55.4, T = 2.4;         // card mm
  // Numeric check (playbook §5): the marking DXF's logo face aligns with the STL silhouette only
  // when mirrored against the y=0 view, so the FRONT face is the STL's y-max side. Standing the
  // card up with rotation.x = -90° maps +y to -z, hence A sits at NEGATIVE z.
  const FZ = -1;
  const nz = (side) => (side === 'A' ? FZ : -FZ);
  const KIT_OFF = [7.66, 0, 2.64];           // kit-file origin -> card-STL origin (cm)
  const PIVOT = [7.66, 2.64];                // rotate-lock disc centre in card-STL cm (x, z)
  const KITS = {
    // swing: -1 -> plates sweep out along the LOWER arc (tools folded on the -x side of the pivot,
    // so a positive rotation would carry them over the top; the real card opens downward)
    bike: { file: 'https://mrf.tools/cdn/shop/files/mrf26-u5-kit-bike.stl?v=b3', colour: 0xF0C526, label: 'BIKE', front: 'BIKE WRENCHES', back: 'PLIERS', open: 'front', swing: { front: -1, back: -1 } },
    military: { file: 'https://mrf.tools/cdn/shop/files/mrf26-u5-kit-military.stl?v=b3', colour: 0x55603C, label: 'MILITARY', front: 'AR15 TOOL', back: 'AK47 TOOL', open: 'front', swing: { front: -1, back: -1 } }
  };

  function spot(t, mark) {
    if (mark && mark.mx != null) return { mx: mark.mx, my: mark.my, side: mark.side, mine: true };
    const CM = window.CardMap;
    const mm = CM ? CM.mmOf('u5', t) : { mx: t.mx || 0, my: t.my || 0 };
    return { mx: mm.mx, my: mm.my, side: t.side, mine: !t.est };
  }

  function parseSTL(buf) {
    const dv = new DataView(buf);
    const tri = dv.getUint32(80, true);
    if (84 + tri * 50 !== buf.byteLength) throw new Error('only binary STL supported');
    const pos = new Float32Array(tri * 9);
    for (let i = 0; i < tri; i++) {
      const o = 84 + i * 50;
      for (let v = 0; v < 3; v++) {
        const p = o + 12 + v * 12, k = i * 9 + v * 3;
        pos[k] = dv.getFloat32(p, true); pos[k + 1] = dv.getFloat32(p + 4, true); pos[k + 2] = dv.getFloat32(p + 8, true);
      }
    }
    return { pos, tri };
  }

  // welded solids -> parts (union-find over shared vertices)
  function shells(pos, tri) {
    const key = (i) => Math.round(pos[i * 3] * 2e4) + '_' + Math.round(pos[i * 3 + 1] * 2e4) + '_' + Math.round(pos[i * 3 + 2] * 2e4);
    const map = new Map(), vid = new Int32Array(tri * 3);
    let nv = 0;
    for (let i = 0; i < tri * 3; i++) { const k = key(i); let id = map.get(k); if (id === undefined) { id = nv++; map.set(k, id); } vid[i] = id; }
    const par = new Int32Array(nv); for (let i = 0; i < nv; i++) par[i] = i;
    const find = (a) => { while (par[a] !== a) { par[a] = par[par[a]]; a = par[a]; } return a; };
    const uni = (a, b) => { a = find(a); b = find(b); if (a !== b) par[b] = a; };
    for (let t = 0; t < tri; t++) { uni(vid[t * 3], vid[t * 3 + 1]); uni(vid[t * 3 + 1], vid[t * 3 + 2]); }
    const g = new Map();
    for (let t = 0; t < tri; t++) { const r = find(vid[t * 3]); if (!g.has(r)) g.set(r, []); g.get(r).push(t); }
    return [...g.values()].map(tris => {
      const p = new Float32Array(tris.length * 9);
      const bb = [1e9, 1e9, 1e9, -1e9, -1e9, -1e9];
      tris.forEach((t, i) => {
        for (let k = 0; k < 9; k++) p[i * 9 + k] = pos[t * 9 + k];
        for (let v = 0; v < 3; v++) {
          const b = t * 9 + v * 3;
          for (let k = 0; k < 3; k++) { bb[k] = Math.min(bb[k], pos[b + k]); bb[k + 3] = Math.max(bb[k + 3], pos[b + k]); }
        }
      });
      return { p, n: tris.length, bb };
    }).sort((a, b) => b.n - a.n);
  }

  class U5Viewer extends HTMLElement {
    connectedCallback() {
      if (this._up) { this._revive(); return; }
      this._up = true;
      this.style.cssText = 'position:relative; display:block; width:100%; height:100%; min-height:280px; background:radial-gradient(120% 100% at 50% 42%, #262C33 0%, #1A1E24 58%, #11141A 100%); touch-action:none;';
      this.innerHTML =
        '<canvas style="display:block; width:100%; height:100%; cursor:grab;"></canvas>' +
        '<svg data-lead style="position:absolute; inset:0; width:100%; height:100%; pointer-events:none; overflow:visible;"><line stroke="#FF6B2C" stroke-width="1.5" stroke-dasharray="4 3" opacity="0"></line><circle r="3" fill="#FF6B2C" opacity="0"></circle></svg>' +
        '<div data-labels style="position:absolute; inset:0; pointer-events:none;"></div>' +
        '<div data-hint style="position:absolute; left:12px; bottom:12px; font-family:\'IBM Plex Mono\',monospace; font-size:11px; letter-spacing:0.1em; color:rgba(236,237,238,0.72); background:rgba(11,12,14,0.72); padding:4px 8px;">LOADING 3D…</div>' +
        '<div data-btns style="position:absolute; right:12px; top:12px; display:flex; gap:6px; flex-wrap:wrap; justify-content:flex-end; max-width:calc(100% - 24px);"></div>' +
        '<div data-kits style="position:absolute; left:12px; top:12px; display:flex; gap:6px; flex-wrap:wrap; max-width:calc(100% - 216px);"></div>';
      this.canvas = this.querySelector('canvas');
      this.labels = this.querySelector('[data-labels]');
      this.lead = this.querySelector('[data-lead] line');
      this.leadDot = this.querySelector('[data-lead] circle');
      this.hint = this.querySelector('[data-hint]');
      this._revive = () => {
        if (!this._tickFn || !this.isConnected) return;
        if (this._beat != null && performance.now() - this._beat < 250) return;
        if (this._raf != null) cancelAnimationFrame(this._raf);
        this._raf = null;
        this._tickFn();
      };
      this._watchdog = setInterval(() => {
        if (this.isConnected && this.clientWidth > 0 && this._tickFn && (this._beat == null || performance.now() - this._beat > 400)) { this._revive(); if (this.refresh && (this._beat == null || performance.now() - this._beat > 400)) this.refresh(); }
      }, 500);
      this._mkBtn = (label, fn, host) => {
        const b = document.createElement('button');
        b.textContent = label;
        b.style.cssText = "cursor:pointer; font-family:'IBM Plex Mono',monospace; font-size:11px; letter-spacing:0.06em; color:#ECEDEE; background:rgba(11,12,14,0.78); border:1px solid #33383F; border-radius:9px; padding:7px 10px;";
        b.onmouseenter = () => { b.style.borderColor = '#FF6B2C'; };
        b.onmouseleave = () => { if (!b.dataset.on) b.style.borderColor = '#33383F'; };
        b.onclick = fn;
        this.querySelector(host || '[data-btns]').appendChild(b);
        return b;
      };
      this.boot().catch(e => { this.hint.textContent = '3D FAILED: ' + e.message; console.error('u5-viewer', e); });
    }

    async boot() {
      const THREE = await import(THREE_URL);
      this.THREE = THREE;
      const renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(devicePixelRatio, 1.75));
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(38, 1, 1, 3000);
      scene.add(new THREE.HemisphereLight(0xf2f4f7, 0x4a5059, 0.55));
      const key = new THREE.DirectionalLight(0xffffff, 1.0); key.position.set(70, 130, 150); scene.add(key);
      const rim = new THREE.DirectionalLight(0xcfe0f5, 1.05); rim.position.set(-90, -50, -130); scene.add(rim);
      const fill = new THREE.DirectionalLight(0xffffff, 0.5); fill.position.set(-40, 90, -60); scene.add(fill);
      (() => {   // studio env — metals read flat black without one
        const c = document.createElement('canvas'); c.width = 512; c.height = 256;
        const x = c.getContext('2d');
        const gr = x.createLinearGradient(0, 0, 0, 256);
        gr.addColorStop(0, '#eef2f7'); gr.addColorStop(0.5, '#9aa3ad'); gr.addColorStop(1, '#2a2e33');
        x.fillStyle = gr; x.fillRect(0, 0, 512, 256);
        x.fillStyle = 'rgba(255,255,255,0.95)'; x.fillRect(60, 30, 150, 60);
        x.fillStyle = 'rgba(210,225,245,0.7)'; x.fillRect(330, 60, 90, 90);
        const tex = new THREE.CanvasTexture(c);
        tex.mapping = THREE.EquirectangularReflectionMapping;
        const pm = new THREE.PMREMGenerator(renderer);
        scene.environment = pm.fromEquirectangular(tex).texture;
        tex.dispose(); pm.dispose();
      })();
      const black = new THREE.MeshStandardMaterial({ name: 'pvd-black', color: 0x17191d, metalness: 0.55, roughness: 0.42, envMapIntensity: 0.7 });
      const steel = new THREE.MeshStandardMaterial({ name: 'steel-440c', color: 0xd2d7dd, metalness: 0.9, roughness: 0.28, envMapIntensity: 1.45 });
      const carrier = new THREE.MeshStandardMaterial({ name: 'kit-carrier', color: 0x1b1e22, metalness: 0.4, roughness: 0.5, envMapIntensity: 1, side: THREE.DoubleSide });
      const hw = new THREE.MeshStandardMaterial({ name: 'pivot-steel', color: 0xB8BEC6, metalness: 0.95, roughness: 0.22, envMapIntensity: 1.5 });

      const card0 = parseSTL(await (await fetch('https://mrf.tools/cdn/shop/files/mrf26-u5.stl?v=b3')).arrayBuffer());
      const cbb = [1e9, 1e9, 1e9, -1e9, -1e9, -1e9];
      for (let i = 0; i < card0.tri * 3; i++) for (let k = 0; k < 3; k++) {
        const v = card0.pos[i * 3 + k]; cbb[k] = Math.min(cbb[k], v); cbb[k + 3] = Math.max(cbb[k + 3], v);
      }
      const scale = W / (cbb[3] - cbb[0]);                       // STL is in cm -> 10
      const off = [-(cbb[0] + cbb[3]) / 2, -(cbb[1] + cbb[4]) / 2, -(cbb[2] + cbb[5]) / 2];
      const mkGeo = (arr, extra) => {
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(arr.slice(), 3));
        if (extra) g.translate(extra[0], extra[1], extra[2]);
        g.translate(off[0], off[1], off[2]);
        g.scale(scale, scale, scale);
        g.computeVertexNormals();
        return g;
      };
      const root = new THREE.Group();
      root.rotation.x = -Math.PI / 2;                            // stand the card up: thickness lands on Z
      scene.add(root);
      const card = new THREE.Mesh(mkGeo(card0.pos), black);
      card.name = 'universal5-card';
      root.add(card);

      const loader = new THREE.TextureLoader();
      const decal = (file, sign) => {
        const tex = loader.load(file, () => { this._dirty = true; });
        tex.anisotropy = 8;
        // the texture is punched by the card's own silhouette, so no engraving hangs over a cutout;
        // sitting it 0.02 mm off the face keeps it from z-fighting without floating visibly
        const m = new THREE.Mesh(new THREE.PlaneGeometry(W, H), new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0.96, depthWrite: false }));
        m.position.z = sign * (T / 2 + 0.02);
        if (sign < 0) m.rotation.y = Math.PI;
        m.name = 'marking-' + (sign > 0 ? 'b' : 'a');
        scene.add(m);
        return m;
      };
      this.decals = [decal('https://mrf.tools/cdn/shop/files/mrf26-u5-decal-a.png?v=b3', nz('A')), decal('https://mrf.tools/cdn/shop/files/mrf26-u5-decal-b.png?v=b3', nz('B'))];

      // ---- KITs: fetched on demand, split into two rotate-lock plates + carrier + hardware
      const px = (PIVOT[0] + off[0]) * scale, pz = (PIVOT[1] + off[2]) * scale;
      const kitCache = {};
      let kitGroup = null, kitName = null;
      const plates = { front: null, back: null };
      const target = { front: 0, back: 0 }, angle = { front: 0, back: 0 };
      const buildKit = async (name) => {
        if (kitCache[name]) return kitCache[name];
        const K = KITS[name];
        const { pos, tri } = parseSTL(await (await fetch(K.file)).arrayBuffer());
        const parts = shells(pos, tri);
        const g = new THREE.Group();
        const tool = new THREE.MeshStandardMaterial({ name: 'kit-' + name, color: K.colour, metalness: 0.3, roughness: 0.45, envMapIntensity: 1, side: THREE.DoubleSide });
        const hinges = {};
        ['front', 'back'].forEach(k => {
          const h = new THREE.Group();
          h.position.set(px, 0, pz);
          g.add(h);
          const inner = new THREE.Group();
          inner.position.set(-px, 0, -pz);
          h.add(inner);
          hinges[k] = { h, inner, sign: (K.swing && K.swing[k]) || 1 };
        });
        parts.forEach(p => {
          const ySpan = [p.bb[1], p.bb[4]], xs = p.bb[3] - p.bb[0], zs = p.bb[5] - p.bb[2];
          const inCardLayer = ySpan[0] > -0.02 && ySpan[1] < 0.26;
          if (inCardLayer && xs > 8 && zs > 5) return;             // the card itself ships from u5.stl
          let mesh;
          if (ySpan[0] >= 0.25) {                                  // plate on the FRONT (logo) side
            mesh = new THREE.Mesh(mkGeo(p.p, KIT_OFF), tool);
            mesh.name = 'plate-front';
            hinges.front.inner.add(mesh);
          } else if (ySpan[1] <= -0.01) {                          // plate on the BACK side
            mesh = new THREE.Mesh(mkGeo(p.p, KIT_OFF), tool);
            mesh.name = 'plate-back';
            hinges.back.inner.add(mesh);
          } else if (inCardLayer && p.n > 200) {
            mesh = new THREE.Mesh(mkGeo(p.p, KIT_OFF), carrier);
            mesh.name = 'kit-carrier';
            g.add(mesh);
          } else {
            mesh = new THREE.Mesh(mkGeo(p.p, KIT_OFF), hw);
            mesh.name = 'pivot-hardware';
            g.add(mesh);
          }
        });
        g.visible = false;
        root.add(g);
        kitCache[name] = { g, hinges, K };
        return kitCache[name];
      };
      const paintKitUI = () => {
        Object.keys(KITS).forEach(n => {
          const b = kitBtns[n];
          const on = kitName === n;
          b.dataset.on = on ? '1' : '';
          b.style.borderColor = on ? '#FF6B2C' : '#33383F';
          b.style.color = on ? '#FF6B2C' : '#ECEDEE';
        });
        ['front', 'back'].forEach(k => {
          const b = plateBtns[k];
          const K = kitName ? KITS[kitName] : null;
          b.style.display = K ? 'block' : 'none';
          if (!K) return;
          const out = target[k] > 0.5;
          b.textContent = (out ? '◂ FOLD ' : '▸ OPEN ') + K[k];
          b.dataset.on = out ? '1' : '';
          b.style.borderColor = out ? '#FF6B2C' : '#33383F';
          b.style.color = out ? '#FF6B2C' : '#ECEDEE';
        });
        this._dirty = true;   // a kit swap / plate flip changes meshes, not the camera
        this._wide = !!kitName && (target.front > 0.5 || target.back > 0.5);
        this._fit();
      };
      const setKit = async (name) => {
        if (kitGroup) kitGroup.visible = false;
        if (kitName === name) {                                   // tapping the active kit removes it
          kitName = null; kitGroup = null; plates.front = plates.back = null;
          target.front = target.back = 0; angle.front = angle.back = 0;
          paintKitUI();
          return;
        }
        this.hint.textContent = 'LOADING ' + KITS[name].label + ' KIT…';
        const k = await buildKit(name);
        kitName = name; kitGroup = k.g; kitGroup.visible = true;
        plates.front = k.hinges.front; plates.back = k.hinges.back;
        const which = KITS[name].open;
        target.front = which === 'front' ? 1 : 0;
        target.back = which === 'back' ? 1 : 0;
        angle.front = target.front; angle.back = target.back;      // arrive already swung out
        plates.front.h.rotation.y = angle.front * Math.PI * plates.front.sign;
        plates.back.h.rotation.y = angle.back * Math.PI * plates.back.sign;
        this.hint.textContent = window.__u5cal ? this._calHint : this._spinHint;
        auto = false;                          // hold the face while the kit is on show
        this.flyTo(this._faceNow || 'A');
        paintKitUI();
      };
      window.__u5kit = (name, open) => {
        if (!name) { if (kitName) setKit(kitName); return; }
        if (kitName !== name) setKit(name).then(() => { if (open) { target[open] = 1; paintKitUI(); } });
        else if (open) { target[open] = 1; paintKitUI(); }
      };

      const FRONT_T = (FZ > 0 ? 0 : Math.PI) + 0.35;   // open on the FRONT (logo) face, whichever z-sign it sits on
      let theta = FRONT_T, phi = 1.25, r = 175, drag = null, auto = true, home = FRONT_T, sway = 0;
      this._fit = () => {
        const w = this.clientWidth || 0, h = this.clientHeight || 0;
        if (w < 120 || h < 120) return;          // layout not settled: a transient aspect ratio
        const vFov = camera.fov * Math.PI / 180;  // would bake a wildly wrong distance into r
        const need = Math.max(H / 2 / Math.tan(vFov / 2), W / 2 / Math.tan(vFov / 2) / (w / h));
        this._base = need * 1.3;
        // a plate swung out reaches ~68 mm past the card's right edge: widen AND re-centre on the pair
        if (!this._zoomed) r = Math.min(520, Math.max(95, this._base * (this._wide ? 1.55 : 1)));
      };
      this._fit();
      const PAN = 34;                            // world-x centre of card+deployed plate
      const place = () => {
        const ax = this._wide ? PAN : 0;
        camera.position.set(ax + r * Math.sin(phi) * Math.sin(theta), r * Math.cos(phi), r * Math.sin(phi) * Math.cos(theta));
        camera.lookAt(ax, 0, 0);
      };
      // screen point -> mm on whichever face is turned towards the camera (placement mode)
      this._hit = (cx, cy) => {
        const rect = this.canvas.getBoundingClientRect();
        if (!rect.width || !rect.height || Math.abs(camera.position.z) < 6) return null;
        const side = (camera.position.z > 0) === (FZ > 0) ? 'A' : 'B';
        const v3 = new THREE.Vector3(((cx - rect.left) / rect.width) * 2 - 1, -(((cy - rect.top) / rect.height) * 2 - 1), 0.5);
        v3.unproject(camera);
        const dir = v3.sub(camera.position);
        if (Math.abs(dir.z) < 1e-6) return null;
        const k = (nz(side) * (T / 2) - camera.position.z) / dir.z;
        if (k < 0) return null;
        const wx = camera.position.x + dir.x * k, wy = camera.position.y + dir.y * k;
        const mx = nz(side) > 0 ? wx + W / 2 : W / 2 - wx;
        return { side: side, mx: Math.max(0, Math.min(W, mx)), my: Math.max(0, Math.min(H, H / 2 - wy)) };
      };
      this._dropAt = (cx, cy, n, advance) => {
        const h = this._hit(cx, cy);
        if (!h) return;
        // a live drag must not rebuild the label (that would release pointer capture): move this one in place
        const mk = (this.markers || []).find(m => m.n === n);
        if (mk) {
          const s2 = nz(h.side), MW = 85.4, MH = 55.4, MT = 2.4;
          mk.side = h.side;
          mk.p = [s2 > 0 ? h.mx - MW / 2 : MW / 2 - h.mx, MH / 2 - h.my, s2 * (MT / 2 + 0.2)];
          mk.el.dataset.solid = '1';
          this._dirty = true;
        }
        if (window.__u5place) window.__u5place(n, h.side, h.mx, h.my, advance);
      };

      const pts = new Map();
      let pinch = 0;
      const spread = () => { const a = [...pts.values()]; return Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y); };
      this.canvas.addEventListener('pointerdown', (e) => {
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        auto = false; this._touched = true; this._fly = null;
        this._moved = 0; this._multi = pts.size >= 2;
        if (pts.size === 2) { pinch = spread(); drag = null; }
        else { drag = { x: e.clientX, y: e.clientY }; this.canvas.style.cursor = 'grabbing'; }
        try { this.canvas.setPointerCapture(e.pointerId); } catch (err) {}
      });
      this.canvas.addEventListener('pointermove', (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pts.size >= 2) {
          this._multi = true;
          const d = spread();
          if (pinch > 0 && d > 0) { r = Math.min(560, Math.max(95, r * (pinch / d))); this._zoomed = true; }
          pinch = d;
          return;
        }
        if (!drag) return;
        this._moved += Math.abs(e.clientX - drag.x) + Math.abs(e.clientY - drag.y);
        theta -= (e.clientX - drag.x) * 0.008;
        phi = Math.min(Math.PI - 0.12, Math.max(0.12, phi - (e.clientY - drag.y) * 0.008));
        drag = { x: e.clientX, y: e.clientY };
      });
      const stop = (e) => {
        pts.delete(e.pointerId);
        if (pts.size < 2) pinch = 0;
        if (pts.size === 1) { const a = [...pts.values()][0]; drag = { x: a.x, y: a.y }; }
        if (pts.size === 0) { drag = null; this.canvas.style.cursor = window.__u5cal ? 'crosshair' : 'grab'; }
      };
      this.canvas.addEventListener('pointerup', (e) => {
        const n = window.__u5cal;
        if (n && !this._multi && this._moved < 7 && pts.size <= 1) this._dropAt(e.clientX, e.clientY, n, true);
      });
      this.canvas.addEventListener('pointerup', stop);
      this.canvas.addEventListener('pointercancel', stop);
      // screen-wide pinch (Tool Explorer forwards two-finger gestures from anywhere on the page)
      this.zoomBy = (f) => { this._touched = true; this._zoomed = true; auto = false; this._fly = null; r = Math.min(560, Math.max(95, r * f)); this._dirty = true; };
      this.cancelDrag = () => { drag = null; pts.clear(); pinch = 0; };
      this.canvas.addEventListener('wheel', (e) => { e.preventDefault(); this._touched = true; this._zoomed = true; r = Math.min(560, Math.max(95, r + e.deltaY * 0.25)); }, { passive: false });

      let pt = null, pp = null, pr = null;
      this._dirty = true;
      this.flyTo = (side) => { home = (nz(side) > 0 ? 0 : Math.PI) + 0.35; this._fly = { theta: home, phi: 1.25 }; auto = false; };
      this._snapTo = (side) => { home = (nz(side) > 0 ? 0 : Math.PI) + 0.35; theta = home; phi = 1.25; };
      this._mkBtn('FRONT', () => this.flyTo('A'));
      this._mkBtn('BACK', () => this.flyTo('B'));
      let finish = 'black';
      const fb = this._mkBtn('NATURAL', () => {
        finish = finish === 'black' ? 'steel' : 'black';
        card.material = finish === 'black' ? black : steel;
        this.decals.forEach(d => d.material.color.set(finish === 'black' ? 0xffffff : 0x1b1e22));
        fb.textContent = finish === 'black' ? 'NATURAL' : 'BLACK';
        this._dirty = true;
      });
      fb.click();            // ships natural: flip the toggle once on first paint
      const kitBtns = {}, plateBtns = {};
      Object.keys(KITS).forEach(n => { kitBtns[n] = this._mkBtn('+ ' + KITS[n].label, () => setKit(n), '[data-kits]'); });
      ['front', 'back'].forEach(k => {
        plateBtns[k] = this._mkBtn('', () => { target[k] = target[k] > 0.5 ? 0 : 1; auto = false; paintKitUI(); }, '[data-kits]');
        plateBtns[k].style.display = 'none';
      });
      paintKitUI();

      // synchronous frame — screenshots and hidden tabs get a fresh GL buffer without waiting on RAF
      this._draw = (settle) => {
        if (settle) ['front', 'back'].forEach(k => { if (plates[k]) { angle[k] = target[k]; plates[k].h.rotation.y = angle[k] * Math.PI * plates[k].sign; } });
        place();
        renderer.render(scene, camera);
      };
      this._dbg = () => ({ r: r, theta: theta, phi: phi, cam: camera.position.toArray().map(v => +v.toFixed(1)), kit: kitName, wide: !!this._wide,
        parts: kitGroup ? kitGroup.children.map(c => ({ name: c.name || c.type, vis: c.visible, kids: c.children.length })) : null,
        target: Object.assign({}, target) });
      // only FRONT/BACK stay on the stage; every other control moves to an in-flow bar above it (hub #finishRow),
      // same pattern as the Designer/Professional colour strips — overlays were covering the model on phones
      (() => {
        const host = document.getElementById('finishRow');
        if (!host) return;
        const bar = document.createElement('div');
        bar.setAttribute('data-finish-for', this.id || 'viewer');
        bar.setAttribute('data-actions-bar', '1');
        bar.style.cssText = 'display:flex; align-items:center; gap:8px; flex-wrap:wrap; border:1px solid #262A30; border-radius:12px; padding:8px 12px; background:#121417;';
        const lab = document.createElement('span');
        lab.textContent = 'KITS & FINISH';
        lab.style.cssText = "font-family:'IBM Plex Mono',monospace; font-size:10px; letter-spacing:0.18em; color:#FF6B2C; margin-right:4px;";
        bar.appendChild(lab);
        [fb].concat(Object.keys(kitBtns).map(k => kitBtns[k]), [plateBtns.front, plateBtns.back]).forEach(b => { if (!b) return; b.style.background = 'transparent'; bar.appendChild(b); });
        host.appendChild(bar);
      })();
      this._spinHint = 'DRAG TO SPIN · PINCH OR SCROLL TO ZOOM · TAP A NUMBER';
      this._calHint = 'PLACEMENT · TAP THE CARD TO SET THE ARMED NUMBER · DRAG A MARKER TO NUDGE';
      this.hint.textContent = this._spinHint;
      this.buildLabels();

      const v = new THREE.Vector3(), nrm = new THREE.Vector3(), toCam = new THREE.Vector3();
      // markers move on the compositor (transform), and only when their rounded position actually changed —
      // left/top on 57 chips every frame was relayouting the whole overlay 60x a second
      const put = (mk, x, y, op) => {
        x = Math.round(x * 10) / 10; y = Math.round(y * 10) / 10; op = Math.round(op * 100) / 100;
        if (mk.tx === x && mk.ty === y && mk.op === op) return;
        mk.tx = x; mk.ty = y; mk.op = op;
        mk.el.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0) translate(-50%,-50%)';
        mk.el.style.opacity = String(op);
      };
      const tick = () => {
        if (!this.isConnected) { this._raf = null; return; }
        this._beat = performance.now();
        this._raf = requestAnimationFrame(tick);
        const w = this.clientWidth, h = this.clientHeight;
        if (!w || !h || this._vis === false) return;
        if (this.canvas.width !== Math.round(w * renderer.getPixelRatio()) || this.canvas.height !== Math.round(h * renderer.getPixelRatio())) {
          renderer.setSize(w, h, false);
          camera.aspect = w / h; camera.updateProjectionMatrix();
          this._dirty = true;
          if (!this._touched) this._fit();
        }
        if (auto && !window.__u5cal) {          // idle life, but bounded: the selected face never turns away
          sway += 0.0075;
          const amp = 0.3 * Math.max(0, 1 - sway / 5.4);   // greeting sway decays to rest (~12 s), then the loop costs nothing
          theta = (home == null ? 0.35 : home) + Math.sin(sway) * amp;
          if (amp === 0) { auto = false; theta = home == null ? 0.35 : home; }
        }
        if (this._wide !== this._wideWas) { this._wideWas = this._wide; this._fit(); }
        const calOn = !!window.__u5cal;
        if (calOn !== this._calWas) {
          this._calWas = calOn;
          this.canvas.style.cursor = calOn ? 'crosshair' : 'grab';
          this.hint.textContent = calOn ? this._calHint : this._spinHint;
          this.buildLabels();
        }
        ['front', 'back'].forEach(k => {
          if (!plates[k]) return;
          if (Math.abs(target[k] - angle[k]) > 0.001) {
            this._dirty = true;
            angle[k] += (target[k] - angle[k]) * 0.075;
            plates[k].h.rotation.y = angle[k] * Math.PI * plates[k].sign;
          }
        });
        if (this._fly) {
          let dt = this._fly.theta - theta;
          while (dt > Math.PI) dt -= 2 * Math.PI;
          while (dt < -Math.PI) dt += 2 * Math.PI;
          theta += dt * 0.12; phi += (this._fly.phi - phi) * 0.12;
          if (Math.abs(dt) < 0.01 && Math.abs(this._fly.phi - phi) < 0.01) this._fly = null;
        }
        if (window.__u5block !== this._blk || window.__u5stamp !== this._stamp) this.buildLabels();
        const actNow = window.__u5active;
        if (actNow !== this._act) { this._act = actNow; this.paintActive(); }
        if (!this._dirty && pt !== null && Math.abs(theta - pt) < 1e-4 && Math.abs(phi - pp) < 1e-4 && Math.abs(r - pr) < 1e-4) return;
        this._dirty = false; pt = theta; pp = phi; pr = r;
        place();
        this._faceNow = (camera.position.z > 0) === (FZ > 0) ? 'A' : 'B';
        renderer.render(scene, camera);
        let leadOn = false;
        this.markers.forEach((mk) => {
          v.set(mk.p[0], mk.p[1], mk.p[2]);
          nrm.set(0, 0, nz(mk.side));
          toCam.copy(camera.position).sub(v).normalize();
          const facing = nrm.dot(toCam);
          v.project(camera);
          const sx = (v.x * 0.5 + 0.5) * w, sy = (-v.y * 0.5 + 0.5) * h;
          const on = mk.n === this._act;
          const front = facing > 0.12 && v.z < 1;
          if (!front && !on) { if (mk.vis !== 0) { mk.vis = 0; mk.el.style.display = 'none'; } return; }
          if (mk.vis !== 1) { mk.vis = 1; mk.el.style.display = 'flex'; }
          if (front) {
            put(mk, sx, sy, Math.min(1, 0.35 + facing) * (mk.dim && !on ? 0.55 : 1) * (mk.off && !on ? 0.4 : 1));
            return;
          }
          const dx = sx - w / 2, dy = sy - h / 2, len = Math.hypot(dx, dy) || 1;
          const lx = Math.max(18, Math.min(w - 18, sx + dx / len * 46)), ly = Math.max(18, Math.min(h - 18, sy + dy / len * 46));
          put(mk, lx, ly, 1);
          this.lead.setAttribute('x1', sx); this.lead.setAttribute('y1', sy);
          this.lead.setAttribute('x2', lx); this.lead.setAttribute('y2', ly);
          this.leadDot.setAttribute('cx', sx); this.leadDot.setAttribute('cy', sy);
          leadOn = true;
        });
        const lop = leadOn ? '0.9' : '0';
        if (this._lop !== lop) { this._lop = lop; this.lead.setAttribute('opacity', lop); this.leadDot.setAttribute('opacity', lop); }
      };
      this._tickFn = tick;
      if (window.IntersectionObserver && !this._io) {   // scrolled off the long hub page: stop rendering
        this._io = new IntersectionObserver((es) => {
          const vis = es.some(e => e.isIntersecting);
          if (vis && this._vis === false) { this._dirty = true; this._revive(); }
          this._vis = vis;
        }, { rootMargin: '240px' });
        this._io.observe(this);
      }
      // a mode switch (placement on/off, a new pick) must not wait on the frame loop: the DC calls
      // refresh() and one tick runs synchronously, so cursor, hint, markers and the frame all update
      // even while requestAnimationFrame is throttled (hidden tab, background iframe)
      this.refresh = () => {
        if (!this.isConnected || !this._tickFn) return;
        if (this._raf != null) cancelAnimationFrame(this._raf);
        this._raf = null;
        tick();
      };
      tick();
    }

    buildLabels() {
      if (this._dragN != null) { this.paintActive(); return; }   // never swap the element under a live drag
      const D = window.U5 || { blocks: [] };
      const marks = window.__u5marks || {};
      this.markers = [];
      this.labels.innerHTML = '';
      this._blk = window.__u5block;
      this._stamp = window.__u5stamp;
      // ALWAYS every marker: showing only the open block made switching tabs look like the marks moved.
      // The open block reads at full strength, the rest sit back as context.
      const cal = !!window.__u5cal;
      const W2 = 85.4, H2 = 55.4, T2 = 2.4;
      D.blocks.forEach((b, bi) => b.tools.forEach(t => {
        const off = this._blk != null && bi !== this._blk;
        const f = spot(t, marks[t.n]);
        const solid = f.mine || t.est === false;
        const dim = !solid;                                   // an un-placed guess still shows — just lighter, so it reads as a guess
        const n = nz(f.side);
        const p = [n > 0 ? f.mx - W2 / 2 : W2 / 2 - f.mx, H2 / 2 - f.my, n * (T2 / 2 + 0.2)];
        const el = document.createElement('button');
        el.textContent = String(t.n).padStart(2, '0');
        el.title = t.name;
        el.style.cssText = "position:absolute; left:0; top:0; opacity:0; will-change:transform; pointer-events:auto; cursor:" + (cal ? 'grab' : 'pointer') + "; width:" + (off ? 17 : 26) + "px; height:" + (off ? 17 : 26) + "px; padding:0; border-radius:50%; font-family:'IBM Plex Mono',monospace; font-size:" + (off ? 8 : 10.5) + "px; font-weight:600; display:flex; align-items:center; justify-content:center; border:1.5px " + (solid ? 'solid' : 'dashed') + " rgba(236,237,238,0.8); background:rgba(11,12,14,0.66); color:#ECEDEE; touch-action:none;";
        el.dataset.solid = solid ? '1' : '';
        el.onclick = () => { if (!cal && window.__u5select) window.__u5select(t.n); };
        if (cal) {
          el.onpointerdown = (e) => {
            e.stopPropagation(); e.preventDefault();
            try { el.setPointerCapture(e.pointerId); } catch (err) {}
            this._dragN = t.n;
            if (window.__u5select) window.__u5select(t.n);
          };
          el.onpointermove = (e) => { if (this._dragN === t.n) this._dropAt(e.clientX, e.clientY, t.n, false); };
          el.onpointerup = (e) => { e.stopPropagation(); this._dragN = null; };
          el.onpointercancel = () => { this._dragN = null; };
        }
        this.labels.appendChild(el);
        this.markers.push({ n: t.n, side: f.side, p, el, dim, off });
      }));
      this._dirty = true;
      this.paintActive();
    }

    paintActive() {
      this._dirty = true;
      const act = window.__u5active;
      (this.markers || []).forEach(mk => {
        const on = mk.n === act;
        mk.el.style.borderStyle = mk.el.dataset.solid || on ? 'solid' : 'dashed';
        mk.el.style.borderColor = on ? '#FF6B2C' : 'rgba(236,237,238,0.8)';
        mk.el.style.background = on ? '#FF6B2C' : 'rgba(11,12,14,0.66)';
        mk.el.style.color = on ? '#0B0C0E' : '#ECEDEE';
        mk.el.style.zIndex = on ? '5' : (mk.off ? '1' : '2');   // context never sits on top of the open block
        mk.el.style.width = mk.el.style.height = on ? '30px' : (mk.off ? '17px' : '26px');
      });
      this._act = act;
      const a = (this.markers || []).find(m => m.n === act);
      if (!a || this._dragN != null) return;
      if (this._lastAct === undefined) { this._lastAct = act; if (this._snapTo) this._snapTo(a.side); return; }
      if (this._lastAct !== act) {
        this._lastAct = act;
        if (window.__u5cal && this._faceNow === a.side) return;
        if (this.flyTo) this.flyTo(a.side);
      }
    }

    disconnectedCallback() { if (this._raf != null) cancelAnimationFrame(this._raf); this._raf = null; clearInterval(this._watchdog); if (this._io) { this._io.disconnect(); this._io = null; } }
  }
  if (!window.customElements.get('u5-viewer')) window.customElements.define('u5-viewer', U5Viewer);
})();
